import React, { ReactNode, createContext, useContext } from 'react';
import { gql } from 'graphql-tag';
import { useGetAwsEngagementScoreQuery, BuyerSignal } from 'generated/graphql';
import { useAmpliFeatureFlags } from '@tackle-io/tackle-auth-tools';
import { ProspectFeatureFlags } from 'utils/features';

export const GET_AWS_ENGAGEMENT_SCORE = gql`
  query getAwsEngagementScore($vendorId: String!, $domain: String!) {
    buyerSignalAwsEngagement(vendor_id: $vendorId, domain: $domain) {
      org_id
      domain
      vendor_id
      type
      value
    }
  }
`;

export type AwsEngagementDataContextValue = {
  loading: boolean;
  error?: Error;
  score?: BuyerSignal;
  isAwsEngagementScoreEnabled?: boolean;
};

const AwsEngagementDataContext = createContext<
  AwsEngagementDataContextValue | undefined
>(undefined);

type AwsEngagementDataProviderProps = {
  children: ReactNode;
  vendorId: string;
  website: string;
};

const extractDomain = (website: string): string | null => {
  try {
    let url = website;
    if (!website.startsWith('http://') && !website.startsWith('https://')) {
      url = 'https://' + website;
    }

    const parsedUrl = new URL(url);
    let hostname = parsedUrl.hostname;
    if (hostname.startsWith('www.')) {
      hostname = hostname.substring(4);
    }
    return hostname;
  } catch (e) {
    console.error(`Error parsing URL: ${website}`, e);
    return null;
  }
};

export const AwsEngagementDataProvider: React.FC<
  AwsEngagementDataProviderProps
> = ({ children, vendorId, website }) => {
  const domain = extractDomain(website);

  const {
    loading: loadingEngagementScore,
    error,
    data,
  } = useGetAwsEngagementScoreQuery({
    variables: { vendorId, domain },
  });

  const { flags: ampliFlags, loading: loadingAmplitudeFlags } =
    useAmpliFeatureFlags();
  const isAwsEngagementScoreEnabled =
    ampliFlags[ProspectFeatureFlags.AWS_ENGAGEMENT_SCORE_ENABLED] === 'on';
  const isLoadingEngagementScore =
    loadingAmplitudeFlags ||
    (loadingEngagementScore && isAwsEngagementScoreEnabled) ||
    false;
  return (
    <AwsEngagementDataContext.Provider
      value={{
        loading: isLoadingEngagementScore,
        error,
        score: data?.buyerSignalAwsEngagement,
        isAwsEngagementScoreEnabled,
      }}
    >
      {children}
    </AwsEngagementDataContext.Provider>
  );
};

export const useAwsEngagementData = (): AwsEngagementDataContextValue => {
  const context = useContext(AwsEngagementDataContext);
  if (!context) {
    throw new Error(
      'useAwsEngagementData must be used within a AwsEngagementDataProvider',
    );
  }
  return context;
};
